import { useCallback, useEffect, useRef, useState } from "react";
import { authHttp } from "../services/commonHttpService";
import { apisPath } from "../utils/path";
import dayjs from "dayjs";

export const generateCaptcha = () => {
    try {
        const string = "0123456789abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNOPQRSTUVWXYZ";
        const len = string.length;
        let captcha = "";
        for (let i = 0; i < 6; i += 1) {
            captcha += string[Math.floor(Math.random() * len)];
        }
        return captcha;
    }
    catch (err) {
        // catchError("generateCaptcha", err, false)
    }
};


export const encryptTextApi = async (plaintext) => {
    try {
        if (!plaintext) {
            throw new Error('Plaintext is required for encryption');
        }

        const response = await authHttp.post(apisPath?.encryptAPI, {
            plaintext: plaintext.toString()
        });

        if (!response.data?.success || !response.data?.encryptText) {
            throw new Error(response?.data?.message || 'Failed to encrypt data');
        }
        return response.data.encryptText;
    } catch (error) {
        throw new Error(error?.response?.data?.message || error?.message || 'Encryption failed');
    }
};

export const fetchAbdmAccessToken = async (setIsLoading, setSnackState) => {
    try {
        setIsLoading(true);

        const response = await authHttp.get(apisPath?.abdmAccessToken);

        if (response.data?.success && response?.data?.accessToken) {
            return response.data.accessToken;
        } else {
            throw new Error(response.data?.message || 'Failed to fetch ABDM access token');
        }
    } catch (error) {
        setSnackState({
            open: true,
            message: error?.response?.data?.message || error?.message || 'Failed to fetch ABDM access token',
            severity: 'error',
        });
        return null;
    } finally {
        setIsLoading(false);
    }
};


// Calculate age from date of birth
export const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

// Format date to dd-mm-yyyy
export const formatDate = (dateString) => {
    if (!dateString || dateString === "null" || dateString === "undefined") {
        return "N/A";
    }

    const date = new Date(dateString);

    // Handle invalid date
    if (isNaN(date.getTime())) {
        return "N/A";
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

export const capitalizeWords = (str) => {
    return str
        ? str.replace(/\b\w/g, (char) => char.toUpperCase())
        : "";
};

// Format time from 24hr to 12hr format
export const formatTimeTo12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
};

// Convert 12hr time (from TimePicker/dayjs) to 24hr format for API
export const convertTo24Hour = (time12hr) => {
    if (!time12hr) return "";
    const dayjsTime = dayjs(time12hr);
    return dayjsTime.format("HH:mm");
};

// Format slot time range (e.g., "11:00 AM - 02:00 PM")
export const formatSlotTime = (startTime, endTime) => {
    return `${formatTimeTo12Hour(startTime)} - ${formatTimeTo12Hour(endTime)}`;
};

// Format ABHA ID - mask all except last 4 digits with hyphens
export const formatAbhaId = (abhaId) => {
    if (!abhaId) return "N/A";

    // Remove any existing separators (hyphens or spaces)
    const cleaned = abhaId.replace(/[-\s]/g, '');

    // Get last 4 digits
    const last4 = cleaned.slice(-4);

    // Mask all except last 4 with 'x'
    const masked = 'x'.repeat(cleaned.length - 4) + last4;

    // Format as "xx-xxxx-xxxx-1234" with hyphens
    // Assuming total length is 14 digits (like 91-7704-3502-3039)
    if (masked.length === 14) {
        return `${masked.slice(0, 2)}-${masked.slice(2, 6)}-${masked.slice(6, 10)}-${masked.slice(10, 14)}`;
    }

    // For 12 digit format (without country code)
    if (masked.length === 12) {
        return `${masked.slice(0, 2)}-${masked.slice(2, 6)}-${masked.slice(6, 10)}-${masked.slice(10, 12)}`;
    }

    // If different length, just add spaces every 4 characters
    return masked.match(/.{1,4}/g)?.join(' ') || masked;
};

// Format ABHA ID with hyphens: XX-XXXX-XXXX-XXXX
export const formatAbhaInput = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Limit to 14 digits
    const limited = digits.slice(0, 14);

    // Add hyphens at appropriate positions
    let formatted = '';
    if (limited.length > 0) {
        formatted = limited.slice(0, 2);
        if (limited.length > 2) {
            formatted += '-' + limited.slice(2, 6);
        }
        if (limited.length > 6) {
            formatted += '-' + limited.slice(6, 10);
        }
        if (limited.length > 10) {
            formatted += '-' + limited.slice(10, 14);
        }
    }
    return formatted;
};


// functions for drug name select box

/**
 * Group medicines by cancer_type
 */
export const groupMedicinesByCancerType = (medicines) => {
    if (!Array.isArray(medicines)) return {};

    return medicines.reduce((acc, medicine) => {
        const cancerType = medicine?.cancer_type || "Other";
        if (!acc[cancerType]) {
            acc[cancerType] = [];
        }
        acc[cancerType].push(medicine);
        return acc;
    }, {});
};

/**
 * Get unique medicine names (removing duplicates)
 */
export const getUniqueMedicineNames = (medicines) => {
    if (!Array.isArray(medicines)) return [];

    const uniqueNames = new Set();
    medicines.forEach(medicine => {
        if (medicine?.name) {
            uniqueNames.add(medicine?.name);
        }
    });
    return Array.from(uniqueNames).sort();
};

/**
 * Search medicines by name
 */
export const searchMedicines = (medicines, searchTerm) => {
    if (!Array.isArray(medicines) || !searchTerm) return medicines;

    const term = searchTerm.toLowerCase();
    return medicines.filter(medicine =>
        medicine?.name?.toLowerCase().includes(term) ||
        medicine?.cancer_type?.toLowerCase().includes(term) ||
        medicine?.common_protocol_names?.toLowerCase().includes(term)
    );
};


// Check if chemotherapy administration is allowed based on session date
export const shouldAllowChemoAdmin = (sessionDate) => {
    if (!sessionDate) return true; // If no date provided, allow by default

    const chemoDate = new Date(sessionDate);
    const currentDate = new Date();

    // Reset time parts to compare only dates
    chemoDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    // Allow if session date is less than or equal to current date
    return chemoDate <= currentDate;
};

// ==================== DATE VALIDATION HELPERS ====================

/**
 * Check if a date is not in the future (for validations)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean} - true if date is today or in the past
 */
export const isDateNotFuture = (dateString) => {
    if (!dateString) return true; // Allow empty dates

    try {
        // Parse the date string (YYYY-MM-DD) to avoid timezone issues
        const [year, month, day] = dateString.split('-').map(Number);
        const selectedDate = new Date(year, month - 1, day);
        selectedDate.setHours(0, 0, 0, 0);

        // Get today's date at midnight
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Compare timestamps
        return selectedDate.getTime() <= today.getTime();
    } catch (error) {
        console.error('Error validating date:', error);
        return false;
    }
};

/**
 * Check if a date is in the future
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean} - true if date is in the future
 */
export const isDateFuture = (dateString) => {
    if (!dateString) return false;

    try {
        const [year, month, day] = dateString.split('-').map(Number);
        const selectedDate = new Date(year, month - 1, day);
        selectedDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return selectedDate.getTime() > today.getTime();
    } catch (error) {
        console.error('Error validating date:', error);
        return false;
    }
};

/**
 * Check if a date is in the past (before today)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean} - true if date is before today
 */
export const isDatePast = (dateString) => {
    if (!dateString) return false;

    try {
        const [year, month, day] = dateString.split('-').map(Number);
        const selectedDate = new Date(year, month - 1, day);
        selectedDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return selectedDate.getTime() < today.getTime();
    } catch (error) {
        console.error('Error validating date:', error);
        return false;
    }
};

/**
 * Check if a date is today
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean} - true if date is today
 */
export const isDateToday = (dateString) => {
    if (!dateString) return false;

    try {
        const [year, month, day] = dateString.split('-').map(Number);
        const selectedDate = new Date(year, month - 1, day);
        selectedDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return selectedDate.getTime() === today.getTime();
    } catch (error) {
        console.error('Error validating date:', error);
        return false;
    }
};

/**
 * Compare two dates
 * @param {string} date1 - First date in YYYY-MM-DD format
 * @param {string} date2 - Second date in YYYY-MM-DD format
 * @returns {number} - -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDates = (date1, date2) => {
    if (!date1 || !date2) return 0;

    try {
        const [year1, month1, day1] = date1.split('-').map(Number);
        const [year2, month2, day2] = date2.split('-').map(Number);

        const d1 = new Date(year1, month1 - 1, day1);
        d1.setHours(0, 0, 0, 0);

        const d2 = new Date(year2, month2 - 1, day2);
        d2.setHours(0, 0, 0, 0);

        const time1 = d1.getTime();
        const time2 = d2.getTime();

        if (time1 < time2) return -1;
        if (time1 > time2) return 1;
        return 0;
    } catch (error) {
        console.error('Error comparing dates:', error);
        return 0;
    }
};

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string} - Today's date
 */
export const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// ==================== END DATE VALIDATION HELPERS ====================

export const useResendOTP = ({
    maxResendCount = 3,
    countdownSeconds = 60,
    onResend = null,
    autoStart = true
} = {}) => {
    const [countdown, setCountdown] = useState(countdownSeconds);
    const [resendCount, setResendCount] = useState(1);
    const [isTimerActive, setIsTimerActive] = useState(autoStart);
    const intervalRef = useRef(null);

    // Format time as MM:SS
    const formattedTime = useCallback(() => {
        const minutes = Math.floor(countdown / 60);
        const seconds = countdown % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }, [countdown]);

    // Check if resend is allowed
    const canResend = countdown === 0 && resendCount < maxResendCount;

    // Timer effect
    useEffect(() => {
        if (isTimerActive && countdown > 0) {
            intervalRef.current = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(intervalRef.current);
                        setIsTimerActive(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isTimerActive, countdown]);

    // Start timer function
    const startTimer = useCallback(() => {
        setCountdown(countdownSeconds);
        setIsTimerActive(true);
    }, [countdownSeconds]);

    // Handle resend
    const handleResend = useCallback(async () => {
        if (!canResend) return;

        try {
            // Execute the onResend callback if provided
            if (onResend && typeof onResend === 'function') {
                await onResend();
            }

            // Only increment count and restart timer if API call succeeded
            setResendCount((prev) => prev + 1);

            // Restart the timer only after successful API call
            startTimer();
        } catch (error) {
            console.error('Resend OTP error:', error);
            // Don't increment count or restart timer if API call failed
            throw error; // Re-throw so calling component can handle it
        }
    }, [canResend, onResend, startTimer]);

    // Reset timer and counter
    const resetTimer = useCallback(() => {
        setCountdown(countdownSeconds);
        setResendCount(1);
        setIsTimerActive(autoStart);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    }, [countdownSeconds, autoStart]);

    return {
        countdown,
        resendCount,
        canResend,
        formattedTime: formattedTime(),
        handleResend,
        resetTimer,
        startTimer,
        isTimerActive,
        maxResendCount
    };
};

/**
 * Common helper to download a blob as a file
 * @param {Blob} blob - The blob data to download
 * @param {string} fileName - The desired name for the downloaded file
 */
export const downloadBlob = (blob, fileName) => {
    if (!blob) return;
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
};

/**
 * Execute API call to download a file with loading and error states
 */
export const executeDownload = async (apiPath, params, fileName, setIsLoading, setSnackState) => {
    try {
        if (setIsLoading) setIsLoading(true);
        const response = await authHttp.get(apiPath, {
            params,
            responseType: 'blob', // Important for handling binary data
        });

        downloadBlob(response.data, fileName);
    } catch (error) {
        if (setSnackState) {
            setSnackState({
                open: true,
                message: error?.response?.data?.message || error?.response?.statusText || error?.message || "Failed to download file",
                severity: "error",
            });
        } else {
            console.error("Download Error:", error);
        }
    } finally {
        if (setIsLoading) setIsLoading(false);
    }
};

// Verhoeff Algorithm tables
const d = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
    [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
    [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
    [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
    [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
    [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
    [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
    [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
    [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];

const p = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
    [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
    [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
    [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
    [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
    [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
    [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

// Verhoeff Algorithm validation
const validateVerhoeff = (num) => {
    let c = 0;
    const myArray = num.split("").reverse().map(Number);

    for (let i = 0; i < myArray.length; i++) {
        c = d[c][p[i % 8][myArray[i]]];
    }
    return c === 0;
};

// Validate Aadhaar using Verhoeff Algorithm
export const validateAadhaar = (aadhaarNumber) => {
    const cleanAadhaar = aadhaarNumber.replace(/\D/g, "");

    // Check if it's 12 digits
    if (cleanAadhaar.length !== 12) {
        return false;
    }

    // Validate using Verhoeff Algorithm
    return validateVerhoeff(cleanAadhaar);
};
